import { PayloadAction } from "@reduxjs/toolkit";
import { ColumnSort } from "@tanstack/react-table";
import moment from "moment";
import { createSearchParams } from "react-router-dom";
import { all, call, put, select, take, takeLatest } from "redux-saga/effects";
import { AppState } from "../../../store";
import fetchAPI, {
  ApiResponse,
  HttpMethod,
  Pagination,
} from "../../../utils/api";
import bookingFilterSlice from "../../booking-filters/store/slice";
import bookingSettingSlice from "../../booking-settings/store/slice";
import { CargoType } from "../../cargo-types/store/slice";
import { Route } from "../../routes/store/slice";
import { Status } from "../../statuses/store/slice";
import { showSuccess } from "../../toaster/store/slice";
import { User } from "../../users/store/slice";
import { VehicleType } from "../../vehicle-types/store/slice";
import bookingPageSlice, { Booking } from "./slice";
import { TrailerType } from "../../trailer-types/store/slice";

type BookingResponse = ApiResponse<{ data: Booking[]; meta: Pagination }>;

function* bookingPageSaga() {
  yield takeLatest(bookingPageSlice.actions.fetchData, handleInitialFetch);
  yield takeLatest(bookingPageSlice.actions.loadMore, handleFetch);
  yield takeLatest(bookingPageSlice.actions.cancel, handleCancel);
  yield takeLatest(bookingFilterSlice.actions.setDepartureTo, tryToFetch);
  yield takeLatest(bookingFilterSlice.actions.setArrivalTo, tryToFetch);
  // yield takeLatest(bookingFilterSlice.actions.setStartTime, tryToFetch);
  // yield takeLatest(bookingFilterSlice.actions.setEndTime, tryToFetch);
  yield takeLatest(bookingFilterSlice.actions.toggleRoute, tryToFetch);
  yield takeLatest(bookingFilterSlice.actions.toggleStatus, tryToFetch);
  yield takeLatest(bookingFilterSlice.actions.toggleVehicleType, tryToFetch);
  yield takeLatest(bookingFilterSlice.actions.toggleTrailerType, tryToFetch);
  yield takeLatest(bookingFilterSlice.actions.toggleCargoType, tryToFetch);
  yield takeLatest(bookingFilterSlice.actions.toggleArchived, tryToFetch);
  yield takeLatest(bookingFilterSlice.actions.toggleCreatedByMe, tryToFetch);
  yield takeLatest(bookingFilterSlice.actions.toggleImported, tryToFetch);
  yield takeLatest(bookingFilterSlice.actions.setCustomers, tryToFetch);
  yield takeLatest(bookingFilterSlice.actions.toggleCustomer, tryToFetch);
  yield takeLatest(bookingFilterSlice.actions.setSearchFilter, tryToFetch);
  yield takeLatest(bookingSettingSlice.actions.setSorting, tryToFetch);
  yield takeLatest(bookingSettingSlice.actions.setPerPage, tryToFetch);
}

function* checkFiltersAndSettings() {
  const settingsReady: boolean = yield select(
    (state: AppState) => state.bookingSettings.isReady
  );
  const filtersReady: boolean = yield select(
    (state: AppState) => state.bookingFilters.isReady
  );

  return settingsReady && filtersReady;
}

function* tryToFetch() {
  const filtersAndSettingsReady: boolean = yield call(checkFiltersAndSettings);

  if (filtersAndSettingsReady) {
    yield call(resetBeforeFetch);
  }
}

function* resetBeforeFetch() {
  yield put(bookingPageSlice.actions.resetData());

  yield put(bookingPageSlice.actions.setIsLoading(true));

  yield call(handleFetch);
}

function* handleInitialFetch() {
  const filtersAndSettingsReady: boolean = yield call(checkFiltersAndSettings);

  if (!filtersAndSettingsReady) {
    const ready: any[] = yield all([
      take(bookingFilterSlice.actions.setReady),
      take(bookingSettingSlice.actions.setReady),
    ]);

    if (ready) {
      yield call(handleFetch);
    }
  } else {
    yield call(handleFetch);
  }
}

function* handleFetch() {
  const meta: Pagination | null = yield select(
    (state: AppState) => state.bookingPage.meta
  );
  const perPage: string = yield select(
    (state: AppState) => state.bookingSettings.perPage
  );
  const page = meta && meta.current_page ? meta.current_page : 1;
  const isArchived: boolean = yield select(
    (state: AppState) => state.bookingFilters.archived
  );
  const isCreatedByMe: boolean = yield select(
    (state: AppState) => state.bookingFilters.createdByMe
  );
  const isImported: boolean = yield select(
    (state: AppState) => state.bookingFilters.imported
  );
  const statuses: Status[] = yield select(
    (state: AppState) => state.bookingFilters.selectedStatuses
  );
  const vehicleTypes: VehicleType[] = yield select(
    (state: AppState) => state.bookingFilters.selectedVehicleTypes
  );
  const trailerTypes: TrailerType[] = yield select(
    (state: AppState) => state.bookingFilters.selectedTrailerTypes
  );
  const cargoTypes: CargoType[] = yield select(
    (state: AppState) => state.bookingFilters.selectedCargoTypes
  );
  const search: string = yield select(
    (state: AppState) => state.bookingFilters.searchFilter
  );
  const sorting: ColumnSort[] = yield select(
    (state: AppState) => state.bookingSettings.sorting
  );
  const departureFrom: string = yield select(
    (state: AppState) => state.bookingFilters.departureFrom
  );
  const departureTo: string = yield select(
    (state: AppState) => state.bookingFilters.departureTo
  );
  const arrivalFrom: string = yield select(
    (state: AppState) => state.bookingFilters.arrivalFrom
  );
  const arrivalTo: string = yield select(
    (state: AppState) => state.bookingFilters.arrivalTo
  );
  const routes: Route[] = yield select(
    (state: AppState) => state.bookingFilters.selectedRoutes
  );
  const customers: User[] = yield select(
    (state: AppState) => state.bookingFilters.selectedCustomers
  );

  const params = createSearchParams({
    page: page.toString(),
    per_page: perPage,
    is_archived: Number(isArchived).toString(),
    is_created_by_me: Number(isCreatedByMe).toString(),
    is_imported: Number(isImported).toString(),
    status_ids: statuses.map((status) => status.id).join(","),
    vehicle_type_ids: vehicleTypes.map((type) => type.id).join(","),
    trailer_type_ids: trailerTypes.map((type) => type.id).join(","),
    cargo_type_ids: cargoTypes.map((type) => type.id).join(","),
    search,
    sort_by: sorting && sorting.length > 0 ? sorting[0].id : "",
    sort_direction:
      sorting && sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : "",
    departure_from: departureFrom
      ? moment(departureFrom).format("YYYY-MM-DD")
      : "",
    departure_to: departureTo ? moment(departureTo).format("YYYY-MM-DD") : "",
    arrival_from: arrivalFrom ? moment(arrivalFrom).format("YYYY-MM-DD") : "",
    arrival_to: arrivalTo ? moment(arrivalTo).format("YYYY-MM-DD") : "",
    route_ids: routes.map((route) => route.id).join(","),
    customer_ids: customers.map((customer) => customer.id).join(","),
  }).toString();

  const response: BookingResponse = yield call(fetchAPI, `/bookings?${params}`);

  if (response && response.data && response.meta) {
    if (page === 1) {
      yield put(bookingPageSlice.actions.setData(response));
    } else {
      yield put(bookingPageSlice.actions.addData(response));
    }
  }

  yield put(bookingPageSlice.actions.setIsLoading(false));
}

function* handleCancel({ payload: id }: PayloadAction<number>) {
  const response: BookingResponse = yield call(
    fetchAPI,
    `/bookings/${id}/cancel`,
    {
      method: HttpMethod.POST,
    }
  );

  if (response && response.data) {
    yield put(showSuccess("Cancellation requested."));

    yield call(resetBeforeFetch);
  }
}

export default bookingPageSaga;
